import type { DeleteControlsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { invalidateChangeRequestFeeds } from '../invalidateChangeRequestFeeds';

const mapTrpcResponseToGraphQL = (
  affectedRows: number
): DeleteControlsMutation => {
  return {
    deleteControlsById: {
      __typename: 'GenericMutationOutput',
      affected_rows: affectedRows,
    },
  };
};

export const useDeleteControlsTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.control.delete.mutationOptions({
      onSuccess: async (result) => {
        // RSP-5789 — the bulk gate partitions the batch; any ids that
        // required approval got a CR created server-side. Surface those on
        // the CR read surfaces immediately instead of waiting for the
        // panel's next poll.
        if (result.changeRequestIds.length > 0) {
          await invalidateChangeRequestFeeds(queryClient, trpc);
        }
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.controlById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.controlsBasic.queryKey(),
        });
      },
    }),
  });

  return {
    deleteControls: async (ids: string[]): Promise<DeleteControlsMutation> => {
      const result = await mutation.mutateAsync({ Ids: ids });

      // BulkApprovalWorkflowResult partitions the batch: `applied` carries
      // the rows the data-layer actually deleted (gate-cleared subset);
      // gate-required rows get a CR created server-side and come back in
      // `changeRequestIds` — no error is thrown for them (matching Hasura's
      // partial-delete / partial-approval semantics). The legacy GraphQL
      // shape only reports the deleted count.
      return mapTrpcResponseToGraphQL(result.applied.length);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
